import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang, formatString } from '../i18n/LanguageContext';
import { relationOptions, relationLabel } from '../i18n/relations';
import { QRCodeSVG } from 'qrcode.react';
import { validateRequired } from '../utils/intakeValidation';
import { splitChipInput } from '../utils/chipSplit';
import { useFavicon } from '../utils/useFavicon';
import { detectBrowserTimezone } from '../utils/timezones';
import TimezoneSelect from '../components/TimezoneSelect';
import { PetsEditor, FaithEditor, WorkingLifeEditor } from '../components/IntakeFieldEditors';

const VERSION = '1.14.1';
// #711 — derive the tablet URL from the current page's host instead of
// hardcoding production. On staging.amigomio.app the QR + copy link now
// correctly point to the staging tablet bundle; on amigomio.app they
// stay on production. Defensively defaults to the production URL on
// SSR / non-browser environments where window isn't defined.
const TABLET_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? `${window.location.origin}/app`
  : 'https://amigomio.app/app';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: #FAF8F5; color: #2C2C2C; -webkit-font-smoothing: antialiased; }
  @keyframes fadeInUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .review-page { min-height:100vh; background: radial-gradient(ellipse 80% 60% at 50% 0%, #FFF0E8 0%, #FAF8F5 70%); }
  .review-nav { display:flex; align-items:center; justify-content:space-between; padding:16px 32px; border-bottom:1px solid rgba(0,0,0,0.06); }
  .review-logo { display:inline-flex; align-items:center; gap:8px; font-size:1.1rem; font-weight:700; color:#2C2C2C; text-decoration:none; }
  .review-logo img { height:28px; width:auto; display:block; }
  .review-logo .ver { font-size:0.7rem; color:#aaa; font-weight:400; }
  .review-body { max-width:640px; margin:0 auto; padding:40px 24px 80px; animation:fadeInUp 0.4s ease both; }
  .review-header { text-align:center; margin-bottom:36px; }
  .review-header h1 { font-size:1.8rem; font-weight:700; margin-bottom:8px; }
  .review-header p { color:#666; font-size:1rem; line-height:1.5; }
  .review-section { background:#fff; border-radius:16px; box-shadow:0 2px 16px rgba(0,0,0,0.06); padding:24px; margin-bottom:20px; }
  .review-section h2 { font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#aaa; margin-bottom:16px; }
  .review-field { margin-bottom:16px; }
  .review-field:last-child { margin-bottom:0; }
  .review-field label { display:block; font-size:0.78rem; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:6px; }
  .review-field input, .review-field textarea, .review-field select {
    width:100%; border:1.5px solid #E0D8D0; border-radius:10px; padding:10px 14px;
    font-size:0.95rem; font-family:inherit; color:#2C2C2C; background:#FAFAF8;
    outline:none; transition:border-color 0.2s;
  }
  .review-field input:focus, .review-field textarea:focus, .review-field select:focus { border-color:#E8845A; background:#fff; }
  .review-field textarea { resize:vertical; min-height:80px; }
  .review-list-field { display:flex; flex-direction:column; gap:8px; }
  .review-list-item { display:flex; gap:8px; align-items:center; }
  .review-list-item input { flex:1; }
  .review-list-item button { background:none; border:none; color:#DC3545; font-size:1.1rem; cursor:pointer; padding:4px 8px; border-radius:6px; flex-shrink:0; }
  .review-list-item button:hover { background:#FFF0F0; }
  .review-add-btn { background:none; border:1.5px dashed #D0C8C0; border-radius:8px; color:#888; font-size:0.85rem; font-weight:500; font-family:inherit; padding:8px 14px; cursor:pointer; width:100%; margin-top:6px; transition:all 0.2s; }
  .review-add-btn:hover { border-color:#E8845A; color:#E8845A; }
  .review-actions { display:flex; gap:12px; margin-top:32px; justify-content:center; }
  .review-save-btn { background:#E8845A; color:#fff; border:none; border-radius:12px; padding:14px 36px; font-size:1rem; font-weight:700; font-family:inherit; cursor:pointer; transition:background 0.2s; box-shadow:0 4px 16px rgba(232,132,90,0.35); }
  .review-save-btn:hover { background:#D4724A; }
  .review-save-btn:disabled { background:#ccc; cursor:not-allowed; box-shadow:none; }
  .review-skip-btn { background:#F5F0EB; color:#666; border:1.5px solid #E0D8D0; border-radius:12px; padding:14px 24px; font-size:0.95rem; font-weight:600; font-family:inherit; cursor:pointer; transition:all 0.2s; }
  .review-skip-btn:hover { background:#EDE7E0; }
  .review-toast { position:fixed; bottom:24px; right:24px; background:#28A745; color:#fff; padding:12px 24px; border-radius:12px; font-weight:600; font-size:0.9rem; box-shadow:0 4px 20px rgba(0,0,0,0.15); z-index:9999; animation:fadeInUp 0.3s ease; }
  .review-toast.error { background:#DC3545; }
  .review-loading { text-align:center; padding:80px 24px; color:#888; }
  .confirm-screen { min-height:100vh; display:flex; align-items:center; justify-content:center; background:radial-gradient(ellipse 80% 60% at 50% 0%, #FFF0E8 0%, #FAF8F5 70%); }
  .confirm-card { text-align:center; background:#fff; border-radius:20px; box-shadow:0 4px 32px rgba(0,0,0,0.08); padding:48px 40px; max-width:480px; width:100%; animation:fadeInUp 0.5s ease both; }
  .confirm-card h1 { font-size:1.6rem; font-weight:700; margin:16px 0 8px; color:#2C2C2C; }
  .confirm-card .confirm-sub { color:#666; font-size:1rem; margin-bottom:28px; line-height:1.5; }
  .confirm-url-label { font-size:0.75rem; font-weight:600; text-transform:uppercase; letter-spacing:0.08em; color:#999; margin-bottom:8px; }
  .confirm-url { font-size:1.35rem; font-weight:700; color:#E8845A; margin-bottom:24px; word-break:break-all; }
  .confirm-qr { margin-bottom:28px; }
  .confirm-portal-btn { background:#E8845A; color:#fff; border:none; border-radius:12px; padding:14px 36px; font-size:1rem; font-weight:700; font-family:inherit; cursor:pointer; transition:background 0.2s; box-shadow:0 4px 16px rgba(232,132,90,0.35); }
  .confirm-portal-btn:hover { background:#D4724A; }
`;

// #735 — full list of supported product languages. Kept in lockstep
// with app/services/language.py:LANG_NAMES. Each label is the
// language's name AS WRITTEN IN ITSELF (an English speaker sees
// "Deutsch" not "German") so the dropdown is recognisable to the
// elderly user the family is configuring it for. Pre-fix the list
// was missing fr + pt.
const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
];

// #214 — eight structured-interest categories. Kept in lockstep with
// FIELDS in app/services/structured_interests.py and STRUCTURED_INTEREST_FIELDS
// in Portal.js. Order matters — it's the order families see.
const STRUCTURED_INTEREST_FIELDS = [
  { key: 'music_artists',    label: 'Favourite singers / bands',
    placeholder: 'e.g. Queen, Frank Sinatra' },
  { key: 'music_genres',     label: 'Music styles / genres',
    placeholder: 'e.g. classical, 1950s jazz' },
  { key: 'sports_interests', label: 'Sports / teams',
    placeholder: 'e.g. Liverpool FC, Wimbledon tennis' },
  { key: 'screen_interests', label: 'Films / TV / actors',
    placeholder: 'e.g. Charlie Chaplin, Casablanca' },
  { key: 'places_interests', label: 'Favourite places',
    placeholder: 'e.g. Lake District, Barcelona' },
  { key: 'hobbies',          label: 'Hobbies',
    placeholder: 'e.g. gardening, crosswords' },
  // #711 — `career` interest bucket removed (8 → 7). Captured separately
  // in the Working life section below; old entries rolled forward into
  // memories.identity.career_pride by migration 0067.
  { key: 'other_interests',  label: 'Other interests',
    placeholder: 'anything else worth knowing' },
];

// Convert server's `{field: [{entry, source}, ...]}` (rich) into the
// flat `{field: ['entry', ...]}` shape the form edits in. Falls back
// to empty arrays per field so the UI renders all 8 categories on a
// fresh load.
function flattenStructuredInterests(server) {
  const out = {};
  const src = server || {};
  for (const f of STRUCTURED_INTEREST_FIELDS) {
    const items = Array.isArray(src[f.key]) ? src[f.key] : [];
    out[f.key] = items.map(it => (typeof it === 'string' ? it : (it && it.entry) || '')).filter(Boolean);
  }
  return out;
}

// Count non-empty entries across the 8 categories — drives the
// inline counter shown to families ("3 / 3 minimum").
function countStructured(structured) {
  if (!structured) return 0;
  let n = 0;
  for (const f of STRUCTURED_INTEREST_FIELDS) {
    const arr = structured[f.key];
    if (Array.isArray(arr)) n += arr.filter(v => v && String(v).trim()).length;
  }
  return n;
}

function ListField({ label, value, onChange }) {
  const { t } = useLang();
  const items = Array.isArray(value) ? value : (value ? [value] : []);
  const update = (i, v) => { const a = [...items]; a[i] = v; onChange(a); };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, '']);
  return (
    <div className="review-field">
      <label>{label}</label>
      <div className="review-list-field">
        {items.map((item, i) => (
          <div key={i} className="review-list-item">
            <input value={item} onChange={e => update(i, e.target.value)} />
            <button onClick={() => remove(i)} title={t.intakeReview.common.remove}>✕</button>
          </div>
        ))}
        <button className="review-add-btn" onClick={add}>{t.intakeReview.common.addItem}</button>
      </div>
    </div>
  );
}

// #214 — chip-style entry for one structured-interest category.
// Mirrors Portal.js's InterestField (chips + inline add input + Enter
// to submit) but operates on local state — the IntakeReview form
// batches everything into one PUT on Save, rather than the Portal's
// per-add API call. UX parity matters: families will see the same
// pattern in the Portal afterwards.
function ChipListField({ label, placeholder, entries, onChange }) {
  const { t } = useLang();
  const [draft, setDraft] = useState('');
  const add = () => {
    // #311 — split on commas / semicolons / newlines so a user typing
    // "knitting, gardening, baking" gets three chips, not one chip
    // with literal commas. Helper handles trim + intra-input dedup +
    // dedup against existing entries (case-insensitive).
    const newEntries = splitChipInput(draft, entries);
    if (newEntries.length > 0) onChange([...entries, ...newEntries]);
    setDraft('');
  };
  const remove = (i) => onChange(entries.filter((_, j) => j !== i));
  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } };
  return (
    <div className="review-field">
      <label>{label}</label>
      {entries.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {entries.map((entry, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#FFF0E8', color: '#8A4524',
              padding: '4px 10px', borderRadius: 999,
              fontSize: '0.82rem', fontWeight: 500,
            }}>
              {entry}
              <button
                type="button"
                onClick={() => remove(i)}
                title={t.intakeReview.common.remove}
                style={{
                  background: 'none', border: 'none', color: '#8A4524',
                  cursor: 'pointer', padding: 0, fontSize: '0.95rem',
                  lineHeight: 1,
                }}
              >&times;</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={draft}
          placeholder={placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKey}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          style={{
            background: draft.trim() ? '#E8845A' : '#E0DDD8',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600,
            cursor: draft.trim() ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit',
          }}
        >{t.intakeReview.common.add}</button>
      </div>
    </div>
  );
}

// #1107 — relation options now come from the shared single source of
// truth (web/src/i18n/relations.js), translated per language via
// t.relations. This removes the IntakeReview copy that used to drift
// against Portal + Signup, and adds Boyfriend/Girlfriend/Partner here in
// lockstep with the other consumers.

// #214 hotfix — richer family-member editor. The previous PR-1
// FamilyMemories component used a flat `ListField` of strings, which:
//   (a) lost the relationship structure ("Maria" with no relation tells
//       the companion nothing), and
//   (b) silently failed validation + save — the validator expects
//       {name, ...} objects and the backend skips non-dicts.
// This component edits `data.family_members` as the structured
// {name, relation, relation_to, notes} shape that PUT /review already
// understands, matching the Portal's family table view.
function FamilyEditor({ members, onChange, defaultRelationTo, perspective = 'family' }) {
  const { t } = useLang();  // #1107 — translated relation labels
  const tr = t.intakeReview.family;
  const co = t.intakeReview.common;
  // #741 — self-perspective: the elderly user IS the account holder,
  // so "Whose?" defaults to "you" instead of "your loved one".
  const isSelf = perspective === 'self';
  const update = (i, field, value) => {
    const next = [...members];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const remove = (i) => onChange(members.filter((_, j) => j !== i));
  const add = () => onChange([...members, {
    name: '', relation: 'family member',
    relation_to: defaultRelationTo || '', notes: '',
    // #735 — status defaults to 'alive' for new entries. Marking a
    // family member 'deceased' tells Berta to use past tense and
    // never present-tense check-ins ("How's John this week?" when
    // John passed away is the failure mode this guards against).
    status: 'alive',
  }]);

  return (
    <div>
      {members.length === 0 && (
        <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: 12 }}>
          {tr.empty}
        </p>
      )}
      {members.map((m, i) => (
        <div
          key={i}
          style={{
            background: '#FAFAF8', border: '1px solid #E0D8D0',
            borderRadius: 10, padding: 14, marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.name}</label>
              <input
                value={m.name || ''}
                onChange={e => update(i, 'name', e.target.value)}
                placeholder={tr.namePlaceholder}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.relation}</label>
              <select
                value={m.relation || 'family member'}
                onChange={e => update(i, 'relation', e.target.value)}
                style={{ width: '100%' }}
              >
                {relationOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                {tr.whose} <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa' }}>{isSelf ? tr.whoseHintSelf : tr.whoseHintFamily}</span>
              </label>
              <input
                value={m.relation_to || ''}
                onChange={e => update(i, 'relation_to', e.target.value)}
                placeholder={defaultRelationTo || (isSelf ? tr.whosePlaceholderSelf : tr.whosePlaceholderFamily)}
                style={{ width: '100%' }}
              />
            </div>
            {/* #735 — alive / deceased status. Default 'alive' for new
                entries; existing rows without status are also treated
                as alive (the renderer + system-prompt rule fall back
                safely). Label phrased gently to avoid feeling clinical. */}
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                {tr.stillWithUs}
              </label>
              <select
                value={m.status || 'alive'}
                onChange={e => update(i, 'status', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="alive">{tr.statusAlive}</option>
                <option value="deceased">{tr.statusDeceased}</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              {tr.notes} <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa' }}>{tr.notesHint}</span>
            </label>
            <input
              value={m.notes || ''}
              onChange={e => update(i, 'notes', e.target.value)}
              placeholder={tr.notesPlaceholder}
              style={{ width: '100%' }}
            />
          </div>
          {/* #735 — relation-help readback. Composes the four fields into
              a natural-language sentence so the family can sanity-check
              that "Whose?" was filled in correctly (the most common
              confusion: family member entered as a grandchild of the
              elderly user when they're actually a grandchild of the
              spouse). Only shows when there's a name + relation_to to
              compose with. */}
          {(() => {
            const name = (m.name || '').trim();
            if (!name) return null;
            const relTo = (m.relation_to || '').trim() || (defaultRelationTo || '').trim();
            if (!relTo) return null;
            const relation = relationLabel(t, m.relation || 'family member');
            const status = (m.status || 'alive') === 'deceased' ? 'deceased' : 'alive';
            const phrase = formatString(
              status === 'deceased' ? tr.readbackDeceased : tr.readbackAlive,
              { name, relation, relTo },
            );
            return (
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: '#FFF8F0',
                  border: '1px solid #F0E0D0',
                  borderRadius: 6,
                  fontSize: '0.85rem',
                  fontStyle: 'italic',
                  color: '#8A7050',
                }}
              >
                {phrase}
              </div>
            );
          })()}
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                background: 'none', border: 'none', color: '#c0392b',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                padding: 0, fontFamily: 'inherit',
              }}
            >{co.remove}</button>
          </div>
        </div>
      ))}
      <button className="review-add-btn" type="button" onClick={add}>{tr.addBtn}</button>
    </div>
  );
}

// #214 hotfix — friend status options. Storage values match the
// taxonomy in app/api/intake.py's friend extraction prompt.
const FRIEND_STATUS_OPTIONS = [
  { value: 'in_contact', label: 'Still in touch' },
  { value: 'lost_touch', label: 'Lost touch' },
  { value: 'deceased',   label: 'Passed away' },
];

// Memory keys are stored as `friend_<prefix>_<field>` where <prefix> is
// a slugified first name (matches the backend's `re.sub(r"[^a-z0-9]",
// "_", fname.lower().split()[0])`). Mirror that here so saved friends
// round-trip through the GET cleanly.
function friendPrefix(name) {
  if (!name || !name.trim()) return 'unknown';
  return name.trim().toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, '_') || 'unknown';
}

// Convert server's flat `[{key, value}]` (legacy storage shape) into a
// structured `[{name, location, how_met, shared_interests, status}]`
// list the form edits in.
function flattenFriends(serverList) {
  if (!Array.isArray(serverList)) return [];
  const groups = {};
  const order = [];
  for (const m of serverList) {
    if (!m || !m.key) continue;
    if (m.key === 'friends_raw') continue;  // legacy fallback, not editable here
    const match = m.key.match(/^friend_([^_]+)_(.+)$/);
    if (!match) continue;
    const prefix = match[1];
    const field = match[2];
    if (!groups[prefix]) {
      groups[prefix] = { name: '', location: '', how_met: '', shared_interests: '', status: 'in_contact' };
      order.push(prefix);
    }
    if (field === 'friend_name')             groups[prefix].name = m.value || '';
    else if (field === 'friend_location')    groups[prefix].location = m.value || '';
    else if (field === 'friend_how_met')     groups[prefix].how_met = m.value || '';
    else if (field === 'friend_shared_interests') groups[prefix].shared_interests = m.value || '';
    else if (field === 'friend_status')      groups[prefix].status = m.value || 'in_contact';
  }
  return order.map(p => groups[p]);
}

// Convert structured friends back to flat key/value pairs for PUT.
// Empty fields are omitted so we don't bloat the memories table with
// blanks. Duplicate prefixes (two "Jan"s) get a numeric suffix so each
// friend's keys remain distinct.
function serializeFriends(structured) {
  if (!Array.isArray(structured)) return [];
  const out = [];
  const seenPrefixes = {};
  for (const fr of structured) {
    if (!fr || !fr.name || !fr.name.trim()) continue;  // skip blanks
    let prefix = friendPrefix(fr.name);
    if (seenPrefixes[prefix]) {
      seenPrefixes[prefix] += 1;
      prefix = `${prefix}${seenPrefixes[prefix]}`;
    } else {
      seenPrefixes[prefix] = 1;
    }
    const push = (field, value) => {
      if (value && String(value).trim()) {
        out.push({ key: `friend_${prefix}_${field}`, value: String(value).trim() });
      }
    };
    push('friend_name', fr.name);
    push('friend_location', fr.location);
    push('friend_how_met', fr.how_met);
    push('friend_shared_interests', fr.shared_interests);
    push('friend_status', fr.status || 'in_contact');
  }
  return out;
}

// #214 hotfix — friends editor matches the FamilyEditor pattern: card
// per friend with Name + Location + How met + Shared interests + Status.
// Friends are optional (not part of the 5-field required floor) but
// give Berta the same kind of relational anchors family does.
function FriendsEditor({ friends, onChange }) {
  const { t } = useLang();
  const tr = t.intakeReview.friends;
  const co = t.intakeReview.common;
  const update = (i, field, value) => {
    const next = [...friends];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const remove = (i) => onChange(friends.filter((_, j) => j !== i));
  const add = () => onChange([...friends, {
    name: '', location: '', how_met: '', shared_interests: '', status: 'in_contact',
  }]);

  return (
    <div>
      {friends.length === 0 && (
        <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: 12 }}>
          {tr.empty}
        </p>
      )}
      {friends.map((f, i) => (
        <div
          key={i}
          style={{
            background: '#FAFAF8', border: '1px solid #E0D8D0',
            borderRadius: 10, padding: 14, marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.name}</label>
              <input
                value={f.name || ''}
                onChange={e => update(i, 'name', e.target.value)}
                placeholder={tr.namePlaceholder}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.status}</label>
              <select
                value={f.status || 'in_contact'}
                onChange={e => update(i, 'status', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="in_contact">{tr.statusInContact}</option>
                <option value="lost_touch">{tr.statusLostTouch}</option>
                <option value="deceased">{tr.statusDeceased}</option>
              </select>
            </div>
          </div>
          <div className="review-field" style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.location}</label>
            <input
              value={f.location || ''}
              onChange={e => update(i, 'location', e.target.value)}
              placeholder={tr.locationPlaceholder}
              style={{ width: '100%' }}
            />
          </div>
          <div className="review-field" style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.howMet}</label>
            <input
              value={f.how_met || ''}
              onChange={e => update(i, 'how_met', e.target.value)}
              placeholder={tr.howMetPlaceholder}
              style={{ width: '100%' }}
            />
          </div>
          <div className="review-field">
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{tr.sharedInterests}</label>
            <input
              value={f.shared_interests || ''}
              onChange={e => update(i, 'shared_interests', e.target.value)}
              placeholder={tr.sharedInterestsPlaceholder}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                background: 'none', border: 'none', color: '#c0392b',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                padding: 0, fontFamily: 'inherit',
              }}
            >{co.remove}</button>
          </div>
        </div>
      ))}
      <button className="review-add-btn" type="button" onClick={add}>{tr.addBtn}</button>
    </div>
  );
}

// #736 — Chronic / ongoing health conditions editor. One text-line
// per condition. Stays distinct from `cognitive_notes` (which is
// sensitive write-only guard-rail text — never echoed back) and
// from acute health concerns (which are admin-only audit data
// from the #735 verify flow). Reviewable + editable by the family
// here because conditions DO change — meds, severity, new diagnoses.
//
// v1 keeps each entry as a single freeform line ("Type 2 diabetes
// since 2015, well-controlled"). The initial intake capture via
// Mia uses Haiku to coerce more-structured prose into per-row
// strings; subsequent family edits are text-only. Per-field
// editing (severity / since-year separately) is a noted v2.
function ChronicConditionsEditor({ items, onChange }) {
  const { t } = useLang();
  const tr = t.intakeReview.health;
  const co = t.intakeReview.common;
  const update = (i, value) => {
    const next = [...items];
    next[i] = { ...next[i], value };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, { value: '' }]);

  return (
    <div>
      {items.length === 0 && (
        <p style={{ color: '#999', fontSize: '0.9rem', fontStyle: 'italic', marginBottom: 12 }}>
          {tr.chronicEmpty}
        </p>
      )}
      {items.map((c, i) => (
        <div
          key={c.id || i}
          style={{
            background: '#FAFAF8', border: '1px solid #E0D8D0',
            borderRadius: 10, padding: 14, marginBottom: 10,
          }}
        >
          <input
            value={c.value || ''}
            onChange={e => update(i, e.target.value)}
            placeholder={tr.chronicPlaceholder}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => remove(i)}
              style={{
                background: 'none', border: 'none', color: '#c0392b',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
                padding: 0, fontFamily: 'inherit',
              }}
            >{co.remove}</button>
          </div>
        </div>
      ))}
      <button className="review-add-btn" type="button" onClick={add}>{tr.addCondition}</button>
    </div>
  );
}

// #711 — Motivational welcome note shown at the top of the form-first
// intake. Translations live inline (one place to review tone) rather
// than in web/src/i18n/translations.js. The CLAUDE.md rule-6 language
// completeness gate is satisfied — every language in
// app/services/language.py:LANG_NAMES has a string here. A future
// i18n-consolidation pass can lift these into translations.js if
// other surfaces want to reuse the copy.
const WELCOME_NOTE_BY_LANG = {
  en: {
    heading: "💡 About this intake",
    body: "Lena is the AI companion your loved one will chat with — by default she's called Lena, but you can rename her later from the portal. The more you can share here, the more naturally she'll talk with them: things like favourite music, family stories, what they used to do for work, even pets they've loved. These small details turn a polite chat into a real conversation. Skip anything that doesn't apply or you'd rather not share — everything stays private to the companion, and you can come back to update it any time.",
  },
  nl: {
    heading: "💡 Over deze vragenlijst",
    body: "Lena is de AI-metgezel met wie uw dierbare gaat praten — standaard heet ze Lena, maar u kunt haar later via het portaal een andere naam geven. Hoe meer u hier deelt, hoe natuurlijker ze met hen kan praten: dingen als favoriete muziek, familieverhalen, wat ze vroeger voor werk deden, zelfs huisdieren waar ze van hielden. Die kleine details maken van een beleefd gesprek een écht gesprek. Sla gerust over wat niet van toepassing is of wat u liever niet deelt — alles blijft privé bij de metgezel, en u kunt later altijd terugkomen om dingen aan te passen.",
  },
  de: {
    heading: "💡 Über diesen Fragebogen",
    body: "Lena ist die KI-Begleiterin, mit der sich Ihr Angehöriger unterhalten wird — standardmäßig heißt sie Lena, aber Sie können ihr später über das Portal einen anderen Namen geben. Je mehr Sie hier teilen, desto natürlicher kann sie mit ihnen sprechen: Dinge wie Lieblingsmusik, Familiengeschichten, was sie früher beruflich gemacht haben, sogar Haustiere, die sie geliebt haben. Diese kleinen Details machen aus einem höflichen Gespräch ein echtes. Überspringen Sie ruhig, was nicht passt oder was Sie lieber nicht teilen möchten — alles bleibt privat bei der Begleiterin, und Sie können jederzeit zurückkommen und Anpassungen vornehmen.",
  },
  fr: {
    heading: "💡 À propos de ce questionnaire",
    body: "Lena est la compagne IA avec laquelle votre proche va converser — par défaut elle s'appelle Lena, mais vous pourrez la renommer plus tard depuis le portail. Plus vous partagerez d'informations ici, plus elle pourra lui parler de manière naturelle : musique préférée, histoires de famille, ancien métier, même les animaux qu'il ou elle a aimés. Ces petits détails transforment une conversation polie en un véritable échange. N'hésitez pas à passer ce qui ne s'applique pas ou que vous préférez ne pas partager — tout reste privé entre vous et la compagne, et vous pourrez toujours revenir mettre à jour.",
  },
  es: {
    heading: "💡 Sobre este cuestionario",
    body: "Lena es la compañera de IA con la que va a charlar su ser querido — de forma predeterminada se llama Lena, pero puede cambiarle el nombre más adelante desde el portal. Cuanto más comparta aquí, más natural será la forma en que ella pueda hablarle: cosas como su música favorita, historias familiares, a qué se dedicaba, incluso las mascotas que ha querido. Estos pequeños detalles convierten una charla cortés en una conversación real. Sáltese lo que no aplique o lo que prefiera no compartir — todo queda privado con la compañera, y siempre podrá volver para actualizarlo.",
  },
  it: {
    heading: "💡 A proposito di questo questionario",
    body: "Lena è la compagna IA con cui chiacchiererà la persona a cui vuole bene — di default si chiama Lena, ma potrà cambiarle nome più avanti dal portale. Più cose condividerà qui, più potrà parlare con lui o lei in modo naturale: musica preferita, storie di famiglia, che lavoro faceva, persino gli animali a cui ha voluto bene. Questi piccoli dettagli trasformano una chiacchierata cortese in una conversazione vera. Salti pure ciò che non si applica o che preferisce non condividere — tutto resta privato con la compagna, e potrà sempre tornare ad aggiornare.",
  },
  pt: {
    heading: "💡 Sobre este questionário",
    body: "A Lena é a companheira de IA com quem o seu ente querido vai conversar — por defeito chama-se Lena, mas pode mudar-lhe o nome mais tarde a partir do portal. Quanto mais partilhar aqui, mais natural será a forma como ela poderá conversar com ele ou ela: música preferida, histórias de família, o que fazia profissionalmente, até animais de estimação de quem gostava. Estes pequenos detalhes transformam uma conversa educada num diálogo verdadeiro. Salte o que não se aplicar ou que preferir não partilhar — tudo fica privado com a companheira, e pode voltar mais tarde para atualizar.",
  },
};

// #741 — self-perspective variants of the welcome note. Picked when
// the account's intake_perspective='self'. Same tone constraints as
// the family-perspective copy above (warm + inviting, not patronising
// — no "incomplete = worse" framing). The Lena introduction adapts:
// "the AI companion who'll chat with you" instead of "your loved one
// will chat with".
const WELCOME_NOTE_BY_LANG_SELF = {
  en: {
    heading: "💡 About this intake",
    body: "Lena is the AI companion who'll chat with you — by default she's called Lena, but you can rename her later from the portal. The more you can share here, the more naturally she'll talk with you: favourite music, family stories, what you used to do for work, even pets you've loved. These small details turn a polite chat into a real conversation. Skip anything that doesn't apply or you'd rather not share — everything stays private to the companion, and you can come back to update it any time.",
  },
  nl: {
    heading: "💡 Over deze vragenlijst",
    body: "Lena is de AI-metgezel met wie u gaat praten — standaard heet ze Lena, maar u kunt haar later via het portaal een andere naam geven. Hoe meer u hier deelt, hoe natuurlijker ze met u kan praten: favoriete muziek, familieverhalen, wat u vroeger voor werk deed, zelfs huisdieren waar u van hield. Die kleine details maken van een beleefd gesprek een écht gesprek. Sla gerust over wat niet van toepassing is of wat u liever niet deelt — alles blijft privé bij de metgezel, en u kunt later altijd terugkomen om dingen aan te passen.",
  },
  de: {
    heading: "💡 Über diesen Fragebogen",
    body: "Lena ist die KI-Begleiterin, mit der Sie sich unterhalten werden — standardmäßig heißt sie Lena, aber Sie können ihr später über das Portal einen anderen Namen geben. Je mehr Sie hier teilen, desto natürlicher kann sie mit Ihnen sprechen: Lieblingsmusik, Familiengeschichten, was Sie früher beruflich gemacht haben, sogar Haustiere, die Sie geliebt haben. Diese kleinen Details machen aus einem höflichen Gespräch ein echtes. Überspringen Sie ruhig, was nicht passt oder was Sie lieber nicht teilen möchten — alles bleibt privat bei der Begleiterin, und Sie können jederzeit zurückkommen und Anpassungen vornehmen.",
  },
  fr: {
    heading: "💡 À propos de ce questionnaire",
    body: "Lena est la compagne IA avec laquelle vous allez converser — par défaut elle s'appelle Lena, mais vous pourrez la renommer plus tard depuis le portail. Plus vous partagerez d'informations ici, plus elle pourra vous parler de manière naturelle : musique préférée, histoires de famille, ancien métier, même les animaux que vous avez aimés. Ces petits détails transforment une conversation polie en un véritable échange. N'hésitez pas à passer ce qui ne s'applique pas ou que vous préférez ne pas partager — tout reste privé avec la compagne, et vous pourrez toujours revenir mettre à jour.",
  },
  es: {
    heading: "💡 Sobre este cuestionario",
    body: "Lena es la compañera de IA con la que va a charlar — de forma predeterminada se llama Lena, pero puede cambiarle el nombre más adelante desde el portal. Cuanto más comparta aquí, más natural será la forma en que ella pueda hablarle: su música favorita, historias familiares, a qué se dedicaba, incluso las mascotas que ha querido. Estos pequeños detalles convierten una charla cortés en una conversación real. Sáltese lo que no aplique o lo que prefiera no compartir — todo queda privado con la compañera, y siempre podrá volver para actualizarlo.",
  },
  it: {
    heading: "💡 A proposito di questo questionario",
    body: "Lena è la compagna IA con cui chiacchiererà — di default si chiama Lena, ma potrà cambiarle nome più avanti dal portale. Più cose condividerà qui, più potrà parlare con lei in modo naturale: musica preferita, storie di famiglia, che lavoro faceva, persino gli animali a cui ha voluto bene. Questi piccoli dettagli trasformano una chiacchierata cortese in una conversazione vera. Salti pure ciò che non si applica o che preferisce non condividere — tutto resta privato con la compagna, e potrà sempre tornare ad aggiornare.",
  },
  pt: {
    heading: "💡 Sobre este questionário",
    body: "A Lena é a companheira de IA com quem vai conversar — por defeito chama-se Lena, mas pode mudar-lhe o nome mais tarde a partir do portal. Quanto mais partilhar aqui, mais natural será a forma como ela poderá conversar consigo: música preferida, histórias de família, o que fazia profissionalmente, até animais de estimação de quem gostava. Estes pequenos detalhes transformam uma conversa educada num diálogo verdadeiro. Salte o que não se aplicar ou que preferir não partilhar — tudo fica privado com a companheira, e pode voltar mais tarde para atualizar.",
  },
};

function WelcomeNote({ lang, perspective }) {
  const short = (lang || 'en').split('-')[0].toLowerCase();
  // #741 — pick the perspective-specific dict, then resolve language
  // with a graceful fallback chain (lang → en → family-en).
  const dict = perspective === 'self'
    ? WELCOME_NOTE_BY_LANG_SELF
    : WELCOME_NOTE_BY_LANG;
  const copy = dict[short] || dict.en || WELCOME_NOTE_BY_LANG.en;
  return (
    <div
      style={{
        background: '#FEF1E8',
        borderLeft: '3px solid #E8845A',
        borderRadius: '0 8px 8px 0',
        padding: '14px 18px',
        marginBottom: 24,
        fontSize: '0.93rem',
        lineHeight: 1.55,
        color: '#6c4433',
      }}
    >
      <div style={{ fontWeight: 700, color: '#E8845A', marginBottom: 4 }}>
        {copy.heading}
      </div>
      {copy.body}
    </div>
  );
}

// #711 — three structured editors (Faith / Working life / Pets) live
// in a shared module so the Portal section detail views can reuse the
// exact same UI for inline editing — see web/src/components/IntakeFieldEditors.js.
// Constants exported alongside are also reused by Portal renderers.

// #222 D4 required-field gate moved to web/src/utils/intakeValidation.js
// so it's testable and reusable from the post-form Berta chat (PR-2).

// #227 — personalised follow-up questions screen. Sits between the
// form save and "All set!". Family fills however many they want
// (all-optional). Answers are stored as `intake_narrative` rows so
// Berta sees them as "stories you have always known".
function FollowUpQuestions({ onDone, onSkip }) {
  const { t, lang } = useLang();
  const tr = t.intakeReview.followup;
  const [questions, setQuestions] = useState(null);  // null = loading
  const [answers, setAnswers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('am_token');
  // #741 — read perspective from the cached am_user to flip the
  // header copy. Self users see "These are personal questions for you";
  // family users see the existing "personal to your loved one" wording.
  let _amUserRaw = null;
  try { _amUserRaw = JSON.parse(localStorage.getItem('am_user') || 'null'); } catch { /* ignore */ }
  const isSelf = _amUserRaw && _amUserRaw.intake_perspective === 'self';

  const fetchQuestions = useCallback(async (regen) => {
    setError(null);
    if (regen) setRegenerating(true);
    try {
      // #1107 follow-on — pass the website-selected language so the
      // AI-generated questions come back in the family's language, not
      // English. The family reads and answers these, so the UI language
      // (not the elderly user's companion language) is the correct one.
      const res = await fetch(
        `/api/intake/follow-up-questions?lang=${encodeURIComponent(lang)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || tr.errGenerate);
      const qs = Array.isArray(json.questions) ? json.questions : [];
      setQuestions(qs);
      setAnswers(qs.map(() => ''));
    } catch (e) {
      setError(e.message || tr.errGenerate);
      setQuestions([]);
    } finally {
      setRegenerating(false);
    }
  }, [token, lang]);

  useEffect(() => { fetchQuestions(false); }, [fetchQuestions]);

  const updateAnswer = (i, v) => {
    setAnswers(prev => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = (questions || []).map((q, i) => ({
        question: q,
        answer: (answers[i] || '').trim(),
      })).filter(it => it.answer);
      // No answers? Same as Skip — don't bother the backend.
      if (items.length === 0) { onDone(); return; }
      const res = await fetch('/api/intake/follow-up-answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers: items }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || tr.errSave);
      onDone();
    } catch (e) {
      setError(e.message || tr.errSave);
      setSaving(false);
    }
  };

  // Loading state — first fetch in flight
  if (questions === null) {
    return (
      <div className="review-page">
        <style>{styles}</style>
        <div className="review-loading">
          {tr.loadingTitle}<br />
          <span style={{ fontSize: '0.85rem', color: '#999' }}>{tr.loadingSub}</span>
        </div>
      </div>
    );
  }

  // Empty result (LLM failure, no profile, etc.) — surface a graceful skip
  if (questions.length === 0) {
    return (
      <div className="review-page">
        <style>{styles}</style>
        <div className="review-body" style={{ textAlign: 'center' }}>
          <h1 style={{ marginTop: 40 }}>{tr.emptyTitle}</h1>
          <p style={{ color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
            {formatString(tr.emptyBody, { errorSuffix: error ? ` (${error})` : '' })}
          </p>
          <button className="review-save-btn" onClick={onDone}>{tr.continue}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-page">
      <style>{styles}</style>
      <nav className="review-nav">
        <span className="review-logo">
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" />
          Amigo Mio
          <span className="ver">v{VERSION}</span>
        </span>
      </nav>
      <div className="review-body">
        <div className="review-header">
          <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>💬</div>
          <h1>{tr.headerTitle}</h1>
          <p>
            {isSelf ? tr.headerSubSelf : tr.headerSubFamily}
          </p>
        </div>

        {error && (
          <div role="alert" style={{
            background: '#fff3f3', borderLeft: '4px solid #c0392b',
            padding: '12px 16px', marginBottom: 20, borderRadius: 8,
            color: '#c0392b', fontSize: '0.9rem',
          }}>{error}</div>
        )}

        {questions.map((q, i) => (
          <div key={i} className="review-section">
            <h2 style={{ fontSize: '0.78rem', color: '#888', marginBottom: 6 }}>{formatString(tr.questionN, { n: i + 1 })}</h2>
            <p style={{
              fontSize: '1rem', fontWeight: 500, color: '#2C2C2C',
              marginBottom: 14, lineHeight: 1.45,
            }}>{q}</p>
            <textarea
              value={answers[i] || ''}
              onChange={e => updateAnswer(i, e.target.value)}
              placeholder={tr.answerPlaceholder}
              style={{
                width: '100%', border: '1.5px solid #E0D8D0',
                borderRadius: 10, padding: '10px 14px',
                fontSize: '0.95rem', fontFamily: 'inherit',
                color: '#2C2C2C', background: '#FAFAF8',
                outline: 'none', minHeight: 90, resize: 'vertical',
              }}
            />
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => fetchQuestions(true)}
            disabled={regenerating || saving}
            style={{
              background: 'none', border: 'none',
              color: regenerating ? '#aaa' : '#E8845A',
              fontSize: '0.88rem', fontWeight: 600,
              fontFamily: 'inherit', cursor: regenerating || saving ? 'not-allowed' : 'pointer',
              padding: 0, textDecoration: 'underline',
            }}
          >
            {regenerating ? tr.generating : tr.showDifferent}
          </button>
        </div>

        <div className="review-actions">
          <button
            type="button"
            className="review-skip-btn"
            onClick={onSkip}
            disabled={saving}
          >
            {tr.skip}
          </button>
          <button
            type="button"
            className="review-save-btn"
            onClick={save}
            disabled={saving}
          >
            {saving ? t.intakeReview.common.saving : tr.saveFinish}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IntakeReview() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const tr = t.intakeReview;
  // Public-facing onboarding form — share the public favicon palette.
  useFavicon('public');
  // #741 — perspective is captured on the account at signup and immutable
  // afterwards. Read once from localStorage am_user (stored by Signup /
  // Login) and use it to flip welcome-note copy, the page header, and
  // the "All set!" page wording. Defaults to 'family' for back-compat
  // with sessions opened before the perspective field existed.
  const _amUserRaw = localStorage.getItem('am_user');
  let _amUser = null;
  try { _amUser = _amUserRaw ? JSON.parse(_amUserRaw) : null; } catch { /* ignore */ }
  const perspective = (_amUser && _amUser.intake_perspective === 'self') ? 'self' : 'family';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  // #227 — after a successful save, show the personalised follow-up
  // questions screen before "All set!". Skipping that screen still
  // sets done=true and lands on the same final confirmation.
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [userId, setUserId] = useState(null);
  const [toast, setToast] = useState(null);
  // #222 — derived from the GET response. 'not_started' for fresh
  // accounts, 'in_progress' / 'completed' for ones that have data.
  // Drives the page heading + button label.
  const [intakeStatus, setIntakeStatus] = useState('completed');
  const [validationErrors, setValidationErrors] = useState([]);
  // #317 — fetched on mount of the post-intake "all set" screen so
  // the QR code encodes the per-elderly-user setup URL with token,
  // not the bare landing page. Falls back to TABLET_URL if the
  // fetch fails so the screen still renders something usable.
  const [setupUrl, setSetupUrl] = useState(null);
  const [data, setData] = useState({
    first_name: '', last_name: '', preferred_name: '', nicknames: [],
    birth_year: '', birthday: '', language: 'en', location: '', places_lived: [],
    // #660 — IANA timezone (e.g. "Europe/Amsterdam"). Defaulted to
    // browser-detected value on first load; family can override via
    // the dropdown. Empty string means "use language-derived default".
    timezone: '',
    // #214 — `structured_interests` is now the primary store; the flat
    // `interests` array is kept around for backward compat but the form
    // populates structured exclusively. PUT /review derives both.
    structured_interests: flattenStructuredInterests({}),
    interests: [], blocked_topics: [], family_members: [],
    // #214 hotfix — friends now editable from the form. Stored as
    // structured `[{name, location, how_met, shared_interests, status}]`
    // and serialized to the legacy flat key/value shape on save.
    friends: [],
    joyful_memories: '', companion_name: '', cognitive_notes: '',
    // #711 — four new optional intake topics. Pets is structured (each
    // pet a {name, species, status, note}); the rest are freetext that
    // the backend extracts into structured memory rows in
    // complete_intake. childhood_sensitive_notes carries sensitive=true
    // and never echoes back to the family or the user (write-only).
    pets: [],
    // #711 — faith and career are STRUCTURED (objects). The form
    // round-trips them as dicts; PUT /review decomposes into individual
    // memory rows. Childhood stays freetext on purpose — the prose
    // narrative is what makes reminiscence work.
    faith: { tradition: '', importance: '', practices: '' },
    childhood: '',
    childhood_sensitive_notes: '',
    career: {
      profession_or_role: '',
      employer: '',
      industry: '',
      years_active: '',
      source_of_pride: '',
    },
    // #736 — chronic / ongoing health conditions. List of
    // `{key, value}` rows hydrated from the server (memories with
    // category='health' subtype='chronic_condition'). Each row is
    // edited as a single text field; PUT round-trips the whole array
    // and the backend DELETE+INSERTs the chronic-condition rows.
    // Distinct from `cognitive_notes` (sensitive, write-only freetext
    // for guard rails) and from acute concerns (admin-only, from the
    // #735 verify flow). See system prompt section (7) for Berta's
    // never-raise-proactively rule.
    chronic_conditions: [],
  });

  const token = localStorage.getItem('am_token');

  // #764 Story C — "Recently noticed" tentative-memory panel was
  // originally embedded HERE during initial implementation, but the
  // intake-review screen is a once-through onboarding form: families
  // don't return after Save & Complete. The panel now lives on the
  // per-tile Portal sub-pages (Pets, Interests, Family, Friends) in
  // components/RecentlyNoticedPanel.js, where the family is actually
  // looking. See PR #771 follow-up commit.

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetch('/api/intake/review', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(res => {
        if (res.detail) throw new Error(res.detail);
        setUserId(res.user_id);
        // #214 — server returns structured_interests as
        // {field: [{entry, source}, ...]}. Flatten to {field: [string]}
        // for editing; PUT will round-trip the strings.
        const structured = flattenStructuredInterests(res.structured_interests);
        // #214 hotfix — `res.family_members` is the parsed unified list
        // of {name, relation, relation_to, notes} (server merges legacy
        // `memories.family.*` keys + new family_member JSON values via
        // _parse_family_members). Use it as the source of truth so the
        // form edits structured objects, not bare strings.
        const fams = Array.isArray(res.family_members) ? res.family_members : [];
        // #214 hotfix — friends hydrated from the server's flat
        // [{key, value}] memory rows into structured editor cards.
        const friendsStructured = flattenFriends(res.friend_memories);
        setData(prev => ({
          ...prev,
          ...res.data,
          structured_interests: structured,
          family_members: fams,
          friends: friendsStructured,
          // #736 — top-level `chronic_conditions` array from the GET
          // response. Already in {key, value} shape; round-trip it as
          // is. Default to [] if the server is older than this PR.
          chronic_conditions: Array.isArray(res.chronic_conditions)
            ? res.chronic_conditions
            : [],
          // #660 — keep the timezone the family already saved if any;
          // only pre-fill the browser-detected value when the column
          // is still empty (fresh intake, or an existing intake done
          // before timezone capture shipped).
          timezone:
            (res.data && res.data.timezone)
            || detectBrowserTimezone()
            || '',
        }));
        // #222 — track whether this is a fresh account (form-first)
        // vs reviewing existing data. Drives heading + button copy.
        setIntakeStatus(res.intake_status || 'completed');
        // For fresh accounts pre-fill defaults the GET handler returned
        if (!res.user_id) {
          setData(prev => ({
            ...prev,
            language: res.language || prev.language || 'en',
            companion_name: res.companion_name || prev.companion_name || '',
          }));
        }
        setLoading(false);
      })
      .catch(e => {
        setToast({ msg: e.message || tr.toastLoadError, error: true });
        setLoading(false);
      });
  }, [token, navigate]);

  const set = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const showToast = (msg, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3500);
  };

  const save = async () => {
    // #222 — required-field gate. Block save until the 5-field floor
    // is met. The validator returns specific guidance per field so the
    // family knows exactly what's missing.
    // #214 hotfix — pass `data.family_members` (the structured
    // {name, relation, ...} array the FamilyEditor edits) to BOTH
    // the validator and as the family-list arg. The previous code
    // passed the unrelated `familyMemories` server-side memories list,
    // which silently failed validation for everyone.
    const errors = validateRequired(data, data.family_members, perspective, t);
    setValidationErrors(errors);
    if (errors.length > 0) {
      showToast(errors[0].msg, true);
      // Scroll to top so the validation banner is visible
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      return;
    }

    setSaving(true);
    try {
      // #214 — derive a flat `interests` rollup so the legacy memories
      // path (memories.identity.interest, topic_preferences) stays in
      // sync until consumers migrate. Backend handles structured first,
      // then writes the rollup.
      const flatRollup = STRUCTURED_INTEREST_FIELDS
        .flatMap(f => (data.structured_interests && data.structured_interests[f.key]) || [])
        .filter(v => v && String(v).trim());
      // #214 hotfix — serialize structured friends back to the flat
      // `[{key, value}]` shape PUT /review expects (legacy memory-row
      // storage format).
      const friendKVs = serializeFriends(data.friends);
      const payload = { ...data, interests: flatRollup, friend_memories: friendKVs };
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // #222 — userId can be null on first save for new accounts;
        // backend bootstraps the elderly_user + intake_session row.
        body: JSON.stringify({ user_id: userId, data: payload }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || tr.saveFailed);
      // #227 — go to the personalised follow-up questions screen,
      // not straight to "All set!". The questions screen has its own
      // skip path that lands on the same final confirmation.
      setShowFollowUps(true);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSaving(false);
    }
  };

  // #317 — fetch the per-user setup token when the "all set" screen
  // first renders so the QR encodes the token-bearing URL. Without it,
  // scanning the QR opens the generic login page and the elderly user
  // has to type credentials manually.
  //
  // #711 fix — this useEffect MUST live above the showFollowUps early
  // return below. Pre-#711 it sat after the return, which was a Rules
  // of Hooks violation (when showFollowUps flipped true after save,
  // the early return fired and the hook was skipped → React 18
  // production hook-count mismatch → "Maximum update depth exceeded"
  // error #300 → completely white follow-up-questions page). The fix
  // is order-only; behaviour is identical (the in-effect early return
  // `if (!done) return` keeps the network call gated to the "All
  // set!" screen).
  useEffect(() => {
    if (!done || setupUrl !== null || !token) return;
    fetch('/api/elderly/setup-token', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.setup_url) {
          setSetupUrl(d.setup_url);
        } else {
          console.warn('setup-token endpoint returned no setup_url; falling back to TABLET_URL');
          setSetupUrl(TABLET_URL);
        }
      })
      .catch(e => {
        console.warn('setup-token fetch failed:', e);
        setSetupUrl(TABLET_URL);
      });
  }, [done, setupUrl, token]);

  // #227 — follow-up questions screen between save and "All set!".
  if (showFollowUps && !done) {
    return (
      <FollowUpQuestions
        onDone={() => { setShowFollowUps(false); setDone(true); }}
        onSkip={() => { setShowFollowUps(false); setDone(true); }}
      />
    );
  }

  if (done) {
    const companionName = data.companion_name || 'Your companion';
    const elderlyName = data.preferred_name || data.first_name || 'your loved one';
    const qrValue = setupUrl || TABLET_URL;
    // Display the URL without the protocol to keep the screen tidy.
    const qrDisplay = qrValue.replace(/^https?:\/\//, '');
    return (
      <>
        <style>{styles}</style>
        <div className="confirm-screen">
          <div className="confirm-card">
            <div style={{fontSize:'3rem'}}>🎉</div>
            <h1>{tr.allset.title}</h1>
            {/* #741 — sub-line + tablet-instruction copy branch on perspective.
                Family: "Lena is ready to meet Margaret!" + "Open this on their tablet"
                Self:   "Lena is ready to chat with you!"   + "Open this on your tablet" */}
            <p className="confirm-sub">
              {perspective === 'self'
                ? formatString(tr.allset.subSelf, { companion: companionName })
                : formatString(tr.allset.subFamily, { companion: companionName, name: elderlyName })}
            </p>

            {/* #228 — QR shown by default with explicit tablet
                instructions. Families don't always know what a QR
                code is for; spelling it out reduces support load.
                #317 — QR + visible URL now use the per-user setup
                URL with token so scanning auto-pairs the tablet. */}
            <p className="confirm-url-label">
              {perspective === 'self' ? tr.allset.openTabletSelf : tr.allset.openTabletFamily}
            </p>
            <div className="confirm-qr" style={{ margin: '16px auto 12px' }}>
              <QRCodeSVG value={qrValue} size={180} />
            </div>
            <p className="confirm-url" style={{ marginBottom: 8 }}>{qrDisplay}</p>
            <p style={{
              color: '#666', fontSize: '0.9rem', lineHeight: 1.5,
              maxWidth: 360, margin: '0 auto 16px',
            }}>
              {tr.allset.qrHelp}
            </p>
            {/* #317 — reassurance for families finishing intake without
                the tablet at hand. */}
            <p style={{
              color: '#888', fontSize: '0.85rem', lineHeight: 1.5,
              fontStyle: 'italic',
              maxWidth: 360, margin: '0 auto 28px',
            }}>
              {tr.allset.reassurance}
            </p>

            <button
              className="confirm-portal-btn"
              onClick={() => navigate('/portal')}
              style={{ display: 'block', margin: '0 auto' }}
            >
              {tr.allset.goPortal}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (loading) return (
    <div className="review-page">
      <style>{styles}</style>
      <div className="review-loading">{tr.loading}</div>
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="review-page">
        <nav className="review-nav">
          <span className="review-logo">
            <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" />
            Amigo Mio
            <span className="ver">v{VERSION}</span>
          </span>
        </nav>

        <div className="review-body">
          <div className="review-header">
            <div style={{fontSize:'2.5rem',marginBottom:'12px'}}>{intakeStatus === 'not_started' ? '👋' : '✅'}</div>
            {/* #222 — copy adapts: form-first new account vs reviewing existing data.
                #741 — header + helper copy branches on perspective for the
                first-time form. Review screen is identical between perspectives
                (the user knows what they entered; no need to repeat). */}
            {intakeStatus === 'not_started' ? (
              <>
                <h1>{perspective === 'self' ? tr.header.newTitleSelf : tr.header.newTitleFamily}</h1>
                <p>
                  {perspective === 'self'
                    ? tr.header.newSubSelf
                    : tr.header.newSubFamily}
                </p>
              </>
            ) : (
              <>
                <h1>{tr.header.reviewTitle}</h1>
                <p>{tr.header.reviewSub}</p>
              </>
            )}
          </div>

          {/*
            #711 — single up-front motivational note. Frames effort as
            upside (more grounded conversations), reassures privacy, and
            promises editability. Tone is intentionally warm, not
            pressuring — no "incomplete intake = worse experience"
            framing. Translations are inline rather than in
            translations.js to keep the welcome-note copy + tone
            review localised in this file (single PR review surface,
            no cross-file coordination for copy iteration).
          */}
          <WelcomeNote lang={lang} perspective={perspective} />

          {/* #222 — required-field validation summary. Shown only after a
              save attempt with errors, so the family isn't yelled at on
              page load. */}
          {validationErrors.length > 0 && (
            <div
              role="alert"
              style={{
                background: '#fff3f3',
                borderLeft: '4px solid #c0392b',
                padding: '14px 18px',
                marginBottom: 20,
                borderRadius: 8,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8, color: '#c0392b' }}>
                {tr.validationSummaryTitle}
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#444', lineHeight: 1.5 }}>
                {validationErrors.map((e, i) => (
                  <li key={i}>{e.msg}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Personal */}
          <div className="review-section">
            <h2>{tr.personal.section}</h2>
            <div className="review-field">
              <label>{tr.personal.firstName} <span style={{color:'#c0392b'}}>*</span></label>
              <input value={data.first_name || ''} onChange={e => set('first_name', e.target.value)} />
            </div>
            <div className="review-field">
              <label>{tr.personal.lastName}</label>
              <input value={data.last_name || ''} onChange={e => set('last_name', e.target.value)} />
            </div>
            <div className="review-field">
              {/* #741 — preferred-name label flips to second-person for
                  self-perspective accounts. The Nicknames ListField that
                  used to live below this row was removed in the same
                  iteration — Bas's call was that nicknames added clutter
                  without much downstream value; preferred_name alone is
                  enough for the companion to address the user warmly. */}
              <label>{perspective === 'self'
                ? tr.personal.preferredNameSelf
                : tr.personal.preferredNameFamily}</label>
              <input value={data.preferred_name || ''} onChange={e => set('preferred_name', e.target.value)} />
            </div>
            <div className="review-field">
              <label>{tr.personal.birthday} <span style={{color:'#c0392b'}}>*</span></label>
              {/* #734 — full date capture. The HTML5 date input sends/receives
                  ISO YYYY-MM-DD which matches the backend's `birthday`
                  memory shape exactly. When the family enters a birthday
                  here, the backend auto-schedules a yearly conversation-
                  mode reminder so Lena wishes them on the right day. */}
              <input
                type="date"
                value={data.birthday || ''}
                onChange={e => {
                  const iso = e.target.value;
                  set('birthday', iso);
                  // Keep the legacy birth_year in lockstep so older
                  // readers (age display, era-appropriate music) still
                  // work without an extra fetch.
                  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                    set('birth_year', iso.slice(0, 4));
                  }
                }}
                max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); })() /* #741 — 18+ floor matches validateRequired */}
                min="1900-01-01"
              />
              {/* #741 — live under-18 inline error. The date input's max
                  attribute blocks the picker but doesn't stop manual
                  typing in browsers that allow it, and a user who shrugs
                  off the visual nudge still needs a clear no. Computed
                  inline (same logic as validateRequired) so the message
                  appears the moment the date is picked, not only on
                  save-click. */}
              {(() => {
                const iso = data.birthday;
                if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
                const bdayDate = new Date(`${iso}T00:00:00`);
                const today = new Date();
                let age = today.getFullYear() - bdayDate.getFullYear();
                const m = today.getMonth() - bdayDate.getMonth();
                const d = today.getDate() - bdayDate.getDate();
                if (m < 0 || (m === 0 && d < 0)) age -= 1;
                if (age >= 18) return null;
                return (
                  <div
                    role="alert"
                    style={{
                      marginTop: 8,
                      padding: '10px 14px',
                      background: '#fff3f3',
                      borderLeft: '4px solid #c0392b',
                      borderRadius: 6,
                      color: '#c0392b',
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {perspective === 'self'
                      ? tr.personal.under18Self
                      : tr.personal.under18Family}
                  </div>
                );
              })()}
              <div style={{fontSize:'0.78rem',color:'#888',marginTop:4}}>
                {tr.personal.birthdayHelp}
              </div>
            </div>
            <div className="review-field">
              <label>{tr.personal.language} <span style={{color:'#c0392b'}}>*</span></label>
              <select value={data.language || 'en'} onChange={e => set('language', e.target.value)}>
                {LANG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="review-field">
              <label>{tr.personal.companionName}</label>
              <input value={data.companion_name || ''} onChange={e => set('companion_name', e.target.value)} placeholder={tr.personal.companionNamePlaceholder} />
            </div>
          </div>

          {/* Location */}
          <div className="review-section">
            <h2>{tr.location.section}</h2>
            <div className="review-field">
              <label>{tr.location.cityCountry}</label>
              <input
                value={data.location || ''}
                onChange={e => set('location', e.target.value)}
                placeholder={tr.location.cityCountryPlaceholder}
              />
            </div>
            <div className="review-field">
              <label>{tr.location.timezone}</label>
              <TimezoneSelect
                value={data.timezone}
                onChange={v => set('timezone', v || '')}
              />
              <div style={{fontSize:'0.78rem',color:'#888',marginTop:4}}>
                {tr.location.timezoneHelp}
              </div>
            </div>
            <ListField label={tr.location.otherPlaces} value={data.places_lived} onChange={v => set('places_lived', v)} />
          </div>

          {/* Interests & topics — #214 structured */}
          <div className="review-section">
            <h2>
              {tr.interests.section} <span style={{color:'#c0392b'}}>*</span>{' '}
              <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>
                {tr.interests.sectionHint}
              </span>
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 18, lineHeight: 1.5 }}>
              {tr.interests.help}
              {' '}
              <strong style={{ color: countStructured(data.structured_interests) >= 3 ? '#1a8a4a' : '#c0392b' }}>
                {formatString(tr.interests.minCount, { count: countStructured(data.structured_interests) })}
              </strong>
            </p>
            {STRUCTURED_INTEREST_FIELDS.map(f => (
              <ChipListField
                key={f.key}
                label={tr.interests.buckets[f.key].label}
                placeholder={tr.interests.buckets[f.key].placeholder}
                entries={(data.structured_interests && data.structured_interests[f.key]) || []}
                onChange={v => set('structured_interests', {
                  ...(data.structured_interests || {}),
                  [f.key]: v,
                })}
              />
            ))}
            <ListField label={tr.interests.topicsToAvoid} value={data.blocked_topics} onChange={v => set('blocked_topics', v)} />
          </div>

          {/* Family — #214 hotfix: structured editor */}
          <div className="review-section">
            <h2>{tr.family.section} <span style={{color:'#c0392b'}}>*</span> <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.family.sectionHint}</span></h2>
            <FamilyEditor
              members={Array.isArray(data.family_members) ? data.family_members : []}
              onChange={v => set('family_members', v)}
              defaultRelationTo={data.preferred_name || data.first_name || ''}
              perspective={perspective}
            />
          </div>

          {/* Friends — #214 hotfix: structured editor */}
          <div className="review-section">
            <h2>{tr.friends.section} <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.friends.sectionHint}</span></h2>
            <FriendsEditor
              friends={Array.isArray(data.friends) ? data.friends : []}
              onChange={v => set('friends', v)}
            />
          </div>

          {/* #711 — Pets */}
          <div className="review-section">
            <h2>{tr.pets.section} <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.pets.sectionHint}</span></h2>
            <PetsEditor
              pets={Array.isArray(data.pets) ? data.pets : []}
              onChange={v => set('pets', v)}
            />
          </div>

          {/* #711 — Faith (structured) */}
          <div className="review-section">
            <h2>{tr.faith.section} <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.common.optional}</span></h2>
            <FaithEditor
              value={data.faith}
              onChange={v => set('faith', v)}
              perspective={perspective}
            />
          </div>

          {/* #711 — Childhood */}
          <div className="review-section">
            <h2>{tr.childhood.section} <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.common.optional}</span></h2>
            <div className="review-field">
              <label>{perspective === 'self'
                ? tr.childhood.growUpSelf
                : tr.childhood.growUpFamily}</label>
              <textarea
                value={data.childhood || ''}
                onChange={e => set('childhood', e.target.value)}
                placeholder={perspective === 'self'
                  ? tr.childhood.growUpPlaceholderSelf
                  : tr.childhood.growUpPlaceholderFamily}
              />
            </div>
            <div className="review-field">
              <label>
                {tr.childhood.sensitiveNotes} <span style={{color:'#B85450', fontWeight:600, fontSize:'0.72rem', textTransform:'uppercase', letterSpacing:'0.04em', marginLeft:6}}>{tr.childhood.sensitiveBadge}</span>
              </label>
              <textarea
                value={data.childhood_sensitive_notes || ''}
                onChange={e => set('childhood_sensitive_notes', e.target.value)}
                placeholder={tr.childhood.sensitivePlaceholder}
                style={{borderColor: '#E5C5C3', background: '#FBEFEF'}}
              />
              <div style={{fontSize:'0.78rem',color:'#B85450',marginTop:4}}>
                {tr.childhood.sensitiveHelp}
              </div>
            </div>
          </div>

          {/* #711 — Working life (structured) */}
          <div className="review-section">
            <h2>{tr.working.section} <span style={{fontWeight:400,color:'#888',fontSize:'0.78rem'}}>{tr.common.optional}</span></h2>
            <WorkingLifeEditor
              value={data.career}
              onChange={v => set('career', v)}
              perspective={perspective}
            />
          </div>

          {/* Memories */}
          <div className="review-section">
            <h2>{tr.memories.section}</h2>
            <div className="review-field">
              <label>{tr.memories.label}</label>
              <textarea
                value={data.joyful_memories || ''}
                onChange={e => set('joyful_memories', e.target.value)}
                placeholder={tr.memories.placeholder}
              />
            </div>
          </div>

          {/* Health */}
          <div className="review-section">
            <h2>{tr.health.section}</h2>

            {/* #736 — Chronic / ongoing conditions, rendered above the
                private notes so families read these first. Distinct
                framing tells them this is awareness data, not the
                "never raise" guard rails below. */}
            <div className="review-field">
              <label>{tr.health.ongoing}</label>
              <p style={{ fontSize: '0.82rem', color: '#666', marginTop: -4, marginBottom: 10 }}>
                {tr.health.ongoingHelp}
              </p>
              <ChronicConditionsEditor
                items={data.chronic_conditions || []}
                onChange={v => set('chronic_conditions', v)}
              />
            </div>

            <div className="review-field" style={{ marginTop: 20 }}>
              <label>{tr.health.notesPrivate}</label>
              <p style={{ fontSize: '0.82rem', color: '#666', marginTop: -4, marginBottom: 10 }}>
                {tr.health.notesPrivateHelp}
              </p>
              <textarea
                value={data.cognitive_notes || ''}
                onChange={e => set('cognitive_notes', e.target.value)}
                placeholder={tr.health.notesPrivatePlaceholder}
              />
            </div>
          </div>

          {/* #214 hotfix — "Skip for now" removed: the form-first intake
              cannot be skipped (it IS the only intake mechanism for new
              accounts). Closing the tab and re-logging-in lands the
              family back here, but there's no in-app dismiss path. */}
          <div className="review-actions">
            <button className="review-save-btn" onClick={save} disabled={saving}>
              {saving ? tr.common.saving : tr.saveButton}
            </button>
          </div>
        </div>

        {toast && (
          <div className={`review-toast${toast.error ? ' error' : ''}`}>{toast.msg}</div>
        )}
      </div>
    </>
  );
}
